import { useState, useCallback, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { aiService, GenerateCaptionRequest, ImproveContentRequest, AIResponse, TranslateResponse } from '../services/ai.service';
import { teamQueryKeys } from '@/lib/queryKeys';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { devLog } from '@/utils/logger';
import { formatApiError, isGlobalGatingHandledError } from '@/services/api';

/**
 * useAI Hook
 *
 * React hook for AI features (caption generation, content improvement, hashtag generation)
 * Uses React Query for state management and caching.
 */

// ============================================================================
// Types
// ============================================================================

export interface UseAIOptions {
  onSuccess?: (response: AIResponse) => void;
  onError?: (error: Error) => void;
}

// ============================================================================
// Hook
// ============================================================================

export const useAI = (options?: UseAIOptions) => {
  const queryClient = useQueryClient();
  const { teamId, hasTeam } = useEffectiveTeamId();
  const [lastUsage, setLastUsage] = useState<AIResponse['usage'] | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [tokensExhausted, setTokensExhausted] = useState(false);

  // Outcome of the most recent call, written synchronously in that call's own catch (right before it returns
  // null) and reset at the start of every call, so it cannot go stale.
  // - handled: the global interceptor SHOWED a surface (upsell / tokens modal): the caller must not toast too.
  // - tokensExhausted: INSUFFICIENT_AI_TOKENS. Only callers that render <AITokensExhaustedModal> from this hook's
  //   `tokensExhausted` state (AIMagicPanel) may stay silent for it; everyone else keeps their own message.
  const errorHandledRef = useRef(false);
  const tokensErrorRef = useRef(false);
  const isErrorHandled = useCallback((): boolean => errorHandledRef.current, []);
  const isTokensExhaustedError = useCallback((): boolean => tokensErrorRef.current, []);
  const recordFailure = (error: unknown): void => {
    errorHandledRef.current = isGlobalGatingHandledError(error);
    tokensErrorRef.current = formatApiError(error).code === 'INSUFFICIENT_AI_TOKENS';
  };

  // Clear error message
  const clearError = useCallback(() => {
    errorHandledRef.current = false;
    tokensErrorRef.current = false;
    setErrorMessage(null);
    setTokensExhausted(false);
  }, []);

  // Dismiss tokens exhausted modal
  const dismissTokensExhausted = useCallback(() => {
    setTokensExhausted(false);
  }, []);

  // Generate caption mutation
  const generateMutation = useMutation({
    mutationFn: (data: GenerateCaptionRequest) => aiService.generateCaption(data),
    onSuccess: (response, variables) => {
      setLastUsage(response.usage || null);
      setErrorMessage(null);
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.ai.usage(teamId) });
      if (variables?.brandProfileId) {
        // A brand profile was applied — its usage count/last-used changed server-side.
        queryClient.invalidateQueries({ queryKey: teamQueryKeys.ai.brandProfiles(teamId) });
      }
      options?.onSuccess?.(response);
    },
    onError: (error) => {
      const apiError = formatApiError(error);
      if (apiError.code === 'INSUFFICIENT_AI_TOKENS') {
        setTokensExhausted(true);
        // Keep the message: only AIMagicPanel renders the tokens modal; other callers show `error` inline.
        setErrorMessage(apiError.message);
        return;
      }
      if (isGlobalGatingHandledError(error)) {
        setErrorMessage(null);
        return;
      }
      const message = apiError.message;
      setErrorMessage(message);
      devLog.error('[AI] Caption generation failed:', error);
      options?.onError?.(error as Error);
    },
  });

  // Improve content mutation
  const improveMutation = useMutation({
    mutationFn: (data: ImproveContentRequest) => aiService.improveContent(data),
    onSuccess: (response, variables) => {
      setLastUsage(response.usage || null);
      setErrorMessage(null);
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.ai.usage(teamId) });
      if (variables?.brandProfileId) {
        // A brand profile was applied — its usage count/last-used changed server-side.
        queryClient.invalidateQueries({ queryKey: teamQueryKeys.ai.brandProfiles(teamId) });
      }
      options?.onSuccess?.(response);
    },
    onError: (error) => {
      const apiError = formatApiError(error);
      if (apiError.code === 'INSUFFICIENT_AI_TOKENS') {
        setTokensExhausted(true);
        // Keep the message: only AIMagicPanel renders the tokens modal; other callers show `error` inline.
        setErrorMessage(apiError.message);
        return;
      }
      if (isGlobalGatingHandledError(error)) {
        setErrorMessage(null);
        return;
      }
      const message = apiError.message;
      setErrorMessage(message);
      devLog.error('[AI] Content improvement failed:', error);
      options?.onError?.(error as Error);
    },
  });

  // Generate hashtags mutation
  const hashtagsMutation = useMutation({
    mutationFn: ({ content, platform, count, brandProfileId }: { content: string; platform?: string; count?: number; brandProfileId?: number }) =>
      aiService.generateHashtags(content, platform, count, brandProfileId),
    onSuccess: (_response, variables) => {
      setErrorMessage(null);
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.ai.usage(teamId) });
      if (variables?.brandProfileId) {
        // A brand profile was applied — its usage count/last-used changed server-side.
        queryClient.invalidateQueries({ queryKey: teamQueryKeys.ai.brandProfiles(teamId) });
      }
    },
    onError: (error) => {
      const apiError = formatApiError(error);
      if (apiError.code === 'INSUFFICIENT_AI_TOKENS') {
        setTokensExhausted(true);
        // Keep the message: only AIMagicPanel renders the tokens modal; other callers show `error` inline.
        setErrorMessage(apiError.message);
        return;
      }
      if (isGlobalGatingHandledError(error)) {
        setErrorMessage(null);
        return;
      }
      const message = apiError.message;
      setErrorMessage(message);
      devLog.error('[AI] Hashtag generation failed:', error);
    },
  });

  // Translate content mutation
  const translateMutation = useMutation({
    mutationFn: ({ content, targetLanguage, sourceLanguage }: {
      content: string;
      targetLanguage: string;
      sourceLanguage?: string
    }) => aiService.translateCaption(content, targetLanguage, sourceLanguage),
    onSuccess: () => {
      setErrorMessage(null);
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.ai.usage(teamId) });
    },
    onError: (error) => {
      const apiError = formatApiError(error);
      if (apiError.code === 'INSUFFICIENT_AI_TOKENS') {
        setTokensExhausted(true);
        // Keep the message: only AIMagicPanel renders the tokens modal; other callers show `error` inline.
        setErrorMessage(apiError.message);
        return;
      }
      if (isGlobalGatingHandledError(error)) {
        setErrorMessage(null);
        return;
      }
      const message = apiError.message;
      setErrorMessage(message);
      devLog.error('[AI] Translation failed:', error);
    },
  });

  // Usage query
  const usageQuery = useQuery({
    queryKey: teamQueryKeys.ai.usage(teamId),
    queryFn: () => aiService.getUsage(),
    enabled: hasTeam,
    staleTime: 60000, // Cache for 1 minute
    retry: false,
  });

  // Convenience functions
  const generateCaption = useCallback(
    async (data: GenerateCaptionRequest): Promise<AIResponse | null> => {
      try {
        clearError();
        return await generateMutation.mutateAsync(data);
      } catch (error) {
        recordFailure(error);
        return null;
      }
    },
    [generateMutation, clearError]
  );

  const improveContent = useCallback(
    async (data: ImproveContentRequest): Promise<AIResponse | null> => {
      try {
        clearError();
        return await improveMutation.mutateAsync(data);
      } catch (error) {
        recordFailure(error);
        return null;
      }
    },
    [improveMutation, clearError]
  );

  const generateHashtags = useCallback(
    async (content: string, platform?: string, count?: number, brandProfileId?: number): Promise<string[] | null> => {
      try {
        clearError();
        return await hashtagsMutation.mutateAsync({ content, platform, count, brandProfileId });
      } catch (error) {
        recordFailure(error);
        return null;
      }
    },
    [hashtagsMutation, clearError]
  );

  const translateCaption = useCallback(
    async (content: string, targetLanguage: string, sourceLanguage?: string): Promise<TranslateResponse | null> => {
      try {
        clearError();
        return await translateMutation.mutateAsync({ content, targetLanguage, sourceLanguage });
      } catch (error) {
        recordFailure(error);
        return null;
      }
    },
    [translateMutation, clearError]
  );

  return {
    // State
    isGenerating: generateMutation.isPending,
    isImproving: improveMutation.isPending,
    isGeneratingHashtags: hashtagsMutation.isPending,
    isTranslating: translateMutation.isPending,
    isLoading: generateMutation.isPending || improveMutation.isPending || hashtagsMutation.isPending || translateMutation.isPending,
    error: errorMessage,
    lastUsage,
    usage: usageQuery.data,
    isLoadingUsage: usageQuery.isLoading,

    // Actions
    generateCaption,
    improveContent,
    generateHashtags,
    translateCaption,
    clearError,
    isErrorHandled,
    isTokensExhaustedError,
    tokensExhausted,
    dismissTokensExhausted,
  };
};

export default useAI;
